import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Calendar, BarChart3, Moon, Sun, LogOut, User, Crown, Zap, Clock, AlertTriangle, BookOpen } from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { subscriptionAPI } from '../services/api';
import Footer from './Footer';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const status = await subscriptionAPI.getStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const navItems = [
    { path: '/', icon: Home, label: 'Home', testId: 'nav-home' },
    { path: '/calendar', icon: Calendar, label: 'Calendar', testId: 'nav-calendar' },
    { path: '/journal', icon: BookOpen, label: 'Journal', testId: 'nav-journal' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics', testId: 'nav-analytics' },
    { path: '/profile', icon: User, label: 'Profile', testId: 'nav-profile' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 transition-colors duration-300">
      {/* Trial Reminder Banner */}
      {subscriptionStatus?.status === 'trial' && subscriptionStatus.trial_days_remaining !== undefined && (
        <div className={`w-full py-2 px-4 text-center text-sm font-medium ${
          subscriptionStatus.trial_days_remaining <= 2 
            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
            : subscriptionStatus.trial_days_remaining <= 4
            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
            : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
        }`} data-testid="trial-banner">
          <Link to="/subscription" className="flex items-center justify-center gap-2 hover:underline">
            {subscriptionStatus.trial_days_remaining <= 2 ? (
              <AlertTriangle className="w-4 h-4" />
            ) : (
              <Clock className="w-4 h-4" />
            )}
            <span>
              {subscriptionStatus.trial_days_remaining === 0 
                ? '🔔 Your free trial ends today! Subscribe now to keep your data.'
                : subscriptionStatus.trial_days_remaining === 1
                ? '🔔 Only 1 day left in your free trial! Subscribe now.'
                : `⏰ ${subscriptionStatus.trial_days_remaining} days left in your free trial. Upgrade to Premium!`
              }
            </span>
          </Link>
        </div>
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass-effect border-b border-white/20 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 flex items-center justify-center shadow-2xl hover-glow group cursor-pointer overflow-hidden">
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <span className="relative text-white font-heading font-bold text-sm tracking-tighter z-10 group-hover:scale-110 transition-transform duration-300">
                H2G
              </span>
            </div>
            <h1 className="font-heading text-2xl font-semibold gradient-text" data-testid="app-title">
              Habit2Goals
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Subscription Status Badge */}
            {subscriptionStatus && (
              <Link 
                to="/subscription"
                data-testid="subscription-badge"
                className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105 ${
                  subscriptionStatus.status === 'active' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : subscriptionStatus.status === 'trial'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg animate-pulse'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 hover:text-white'
                }`}
              >
                {subscriptionStatus.status === 'active' ? (
                  <>
                    <Crown className="w-3.5 h-3.5" />
                    <span>Premium</span>
                  </>
                ) : subscriptionStatus.status === 'trial' ? (
                  <>
                    <Zap className="w-3.5 h-3.5" />
                    <span>Trial</span>
                  </>
                ) : (
                  <>
                    <Crown className="w-3.5 h-3.5" />
                    <span>Upgrade</span>
                  </>
                )}
              </Link>
            )}

            {/* User Info */}
            {user && (
              <Link 
                to="/profile"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 hover:from-blue-500/20 hover:to-purple-500/20 transition-all"
              >
                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">{user.name}</span>
              </Link>
            )}
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-button"
              className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-white/10 transition-all duration-200 hover-glow"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 text-purple-600" />
              ) : (
                <Sun className="w-5 h-5 text-yellow-400" />
              )}
            </button>
            
            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="gap-2 hover:bg-white/50 dark:hover:bg-white/10 rounded-xl"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar Navigation */}
      <nav className="hidden md:block fixed left-8 top-32 z-50">
        <div className="flex flex-col gap-2 gradient-card rounded-2xl p-3 shadow-xl">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 relative ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg glow-blue'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-0 max-w-7xl mx-auto px-6 py-8 pb-24 md:pb-8">
        {children}
      </main>

      {/* Footer */}
      <Footer />

      {/* Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden glass-effect border-t border-white/20 dark:border-white/10 z-50 shadow-2xl">
        <div className="flex items-center justify-around px-6 py-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400 bg-gradient-to-br from-blue-500/20 to-purple-500/20'
                    : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 hover:bg-blue-500/10'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
